import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import moment from "moment";
import classnames from "classnames";
import ReactTooltip from "react-tooltip";
import { has, isEmpty } from "lodash";
import io from "socket.io-client";
import Notification, { notifyApiError } from "components/Notification";
import Layout from "layouts/DashboardManage";
import Pagination from "components/Pagination";
import { getClient } from "store/actions/clientAction";
import {
  RETRY_FILE_UPLOAD,
  RETRY_FILE_UPLOAD_JOB,
  getAllFiles,
  getFileUploadStatusCount,
  resetPayload,
  retryFailedJobs
} from "store/actions/fileAction";
import { SpinnerRainbow } from "components/Spinner";
import UploadLog from "./UploadLog";
import { hasProperty } from "helpers/object";
import Filter from "./filter";
import Translate from "helpers/Translate";
import fileStatus from "helpers/fileStatus.json";
import FileDetails from "components/FileHistory/FileDetails";
import RetryUpload from "components/Uploads/retryUpload";
// Styles
import "./uploads.css";

function Upload() {
  const dispatch = useDispatch();
  const params = useParams();
  const client = useSelector(state => state.client.getClient);
  const isLoadingClient = useSelector(state => state.client.isLoadingClient);
  const selectedClient = useSelector(state => state.client.selectedClient);
  const error = useSelector(state => state.file.errorFile);
  const isLoadingFiles = useSelector(state => state.file.isLoadingFile);
  const retryFileUpload = useSelector(state => state.file.retryFileUpload);
  const retryJobResponse = useSelector(state => state.file.retryFileUploads);
  const fileUploadStatusCount = useSelector(
    state => state.file.getFileUploadCount
  );
  const files = useSelector(state => state.file.getFiles);

  const initialStatus = [
    fileStatus.STATUS_READY_TO_UPLOAD,
    fileStatus.STATUS_IN_PROGRESS,
    fileStatus.STATUS_UPLOADED,
    fileStatus.STATUS_UPLOAD_FAILED
  ].join(",");
  const [selectedStatus, setSelectedStatus] = useState(initialStatus);
  const [totalUploaded, setTotalUploaded] = useState(0);
  const [totalQueued, setTotalQueued] = useState(0);
  const [totalInProgress, setTotalInProgress] = useState(0);
  const [totalFailed, setTotalFailed] = useState(0);
  const [queueLiveStatus, setQueueLiveStatus] = useState({});

  // Initial page load...
  useEffect(() => {
    if (selectedClient) {
      dispatch(getFileUploadStatusCount(selectedClient));
      dispatch(
        getAllFiles({
          clients: [selectedClient.id],
          status: initialStatus
        })
      );
    }
    // eslint-disable-next-line
  }, [selectedClient, dispatch]);

  // After a file is sucessfully added to retry-queue
  useEffect(() => {
    if (retryFileUpload) {
      Notification.success(retryFileUpload);
      dispatch(resetPayload(RETRY_FILE_UPLOAD));
    }
  }, [retryFileUpload]);

  // Get Latest data of client from db
  useEffect(() => {
    if (!selectedClient) {
      let lsSelectedClient = localStorage.getItem("selectedClient");
      lsSelectedClient = JSON.parse(lsSelectedClient);
      dispatch(getClient(lsSelectedClient.id));
    }
  }, [selectedClient, dispatch]);

  // Count total files
  useEffect(() => {
    if (!isEmpty(fileUploadStatusCount)) {
      setTotalUploaded(
        hasProperty(() => fileUploadStatusCount[fileStatus.STATUS_UPLOADED], 0)
      );
      setTotalQueued(
        hasProperty(
          () => fileUploadStatusCount[fileStatus.STATUS_READY_TO_UPLOAD],
          0
        )
      );
      setTotalInProgress(
        hasProperty(
          () => fileUploadStatusCount[fileStatus.STATUS_IN_PROGRESS],
          0
        )
      );
      setTotalFailed(
        hasProperty(
          () => fileUploadStatusCount[fileStatus.STATUS_UPLOAD_FAILED],
          0
        )
      );
    }
  }, [fileUploadStatusCount]);

  // Load stats from Socket
  useEffect(() => {
    if (params.clientId) {
      const socket = io(process.env.REACT_APP_SOCKET_URL, {
        transports:
          process.env.NODE_ENV === "development" ? ["polling"] : ["websocket"]
      });

      socket.on(`queue_${params.clientId}`, info => {
        // switch (info.status) {
        //   case fileStatus.STATUS_READY_TO_UPLOAD:
        //     setTotalQueued(oldValue => oldValue + 1);
        //     break;
        //   case fileStatus.STATUS_IN_PROGRESS:
        //     setTotalQueued(oldValue => oldValue - 1);
        //     setTotalInProgress(oldValue => oldValue + 1);
        //     break;
        //   case fileStatus.STATUS_UPLOADED:
        //     setTotalQueued(oldValue => oldValue - 1);
        //     setTotalInProgress(oldValue => oldValue - 1);
        //     setTotalUploaded(oldValue => oldValue + 1);
        //     break;
        //   case fileStatus.STATUS_UPLOAD_FAILED:
        //     setTotalQueued(oldValue => oldValue - 1);
        //     setTotalInProgress(oldValue => oldValue - 1);
        //     setTotalFailed(oldValue => oldValue + 1);
        //     break;
        //   default:
        //     break;
        // }

        // Update the status in the table/grid
        setQueueLiveStatus({
          ...fileStatus,
          [info.fileDocId]: info.status
        });
      });
    }

    // eslint-disable-next-line
  }, [params.clientId, dispatch]);

  // Show api error notification
  useEffect(() => {
    if (error) {
      notifyApiError(error);
    }
  }, [error]);

  // Show notification on retry job add
  useEffect(() => {
    if (retryJobResponse) {
      Notification.success(retryJobResponse);
      dispatch(resetPayload(RETRY_FILE_UPLOAD_JOB));
    }
  }, [retryJobResponse, selectedClient, dispatch]);

  // Pagination change event handler
  const onPageChange = targetPage => {
    const page = targetPage.selected + 1;
    dispatch(
      getAllFiles({
        clients: [selectedClient.id],
        status: selectedStatus,
        page
      })
    );
  };

  const hasData = () => {
    return isLoadingFiles === false && files?.data?.length > 0;
  };
  const handleRetryFailedUploads = () => {
    Notification.confirm(
      Translate({ id: "do_you_want_to_retry_uploading_the_failed_uploads" }),
      {
        onOk: () =>
          dispatch(
            retryFailedJobs(selectedClient.id, fileStatus.STATUS_UPLOAD_FAILED)
          ),
        labelOk: Translate({ id: "yes" }),
        labelCancel: Translate({ id: "no" })
      }
    );
  };

  const handleRefreshList = () => {
    dispatch(getFileUploadStatusCount(selectedClient));
    dispatch(
      getAllFiles({
        clients: [selectedClient.id],
        status: selectedStatus
      })
    );
  };

  return (
    <Layout>
      <div className="upload-logs-container">
        <span
          dangerouslySetInnerHTML={{
            __html: Translate({
              id: "logs_for",
              values: [hasProperty(() => selectedClient.name, "...")]
            })
          }}
        ></span>
        {/* STATS WRAPPER */}
        <div className="stats-wrapper">
          {/* Total Files */}
          <div
            className={classnames("box box-info", {
              selected: selectedStatus.search(",") >= 0
            })}
          >
            <div className="content">
              <div className="section-a">
                <div className="number mb-20">
                  {totalUploaded + totalQueued + totalInProgress + totalFailed}
                </div>
                <div className="description">
                  {Translate({ id: "total_files" })}{" "}
                </div>
              </div>
              <div className="section-b">
                <i className="far fa-clone icon"></i>
              </div>
            </div>
            {selectedClient && (
              <Filter
                client={selectedClient}
                status={initialStatus}
                setSelectedStatus={setSelectedStatus}
              />
            )}
          </div>

          {/* Queued */}
          <div
            className={classnames("box box-warning", {
              selected: selectedStatus === fileStatus.STATUS_READY_TO_UPLOAD
            })}
          >
            <div className="content">
              <div className="section-a">
                <div className="number mb-20"> {Math.abs(totalQueued)}</div>
                <div className="description">
                  {Translate({ id: "total_queued" })}{" "}
                </div>
              </div>
              <div className="section-b">
                <i className="fas fa-hourglass-half icon"></i>
              </div>
            </div>
            {selectedClient && (
              <Filter
                client={selectedClient}
                status={fileStatus.STATUS_READY_TO_UPLOAD}
                setSelectedStatus={setSelectedStatus}
              />
            )}
          </div>

          {/* In Progress */}
          <div
            className={classnames("box box-grey", {
              selected: selectedStatus === fileStatus.STATUS_IN_PROGRESS
            })}
          >
            <div className="content">
              <div className="section-a">
                <div className="number mb-20"> {Math.abs(totalInProgress)}</div>
                <div className="description">
                  {Translate({ id: "in_progress" })}{" "}
                </div>
              </div>
              <div className="section-b">
                <i className="fas fa-file-medical-alt icon"></i>
              </div>
            </div>
            {selectedClient && (
              <Filter
                client={selectedClient}
                status={fileStatus.STATUS_IN_PROGRESS}
                setSelectedStatus={setSelectedStatus}
              />
            )}
          </div>

          {/* Uploaded */}
          <div
            className={classnames("box box-success", {
              selected: selectedStatus === fileStatus.STATUS_UPLOADED
            })}
          >
            <div className="content">
              <div className="section-a">
                <div className="number mb-20"> {Math.abs(totalUploaded)}</div>
                <div className="description">
                  {Translate({ id: "total_uploaded" })}{" "}
                </div>
              </div>
              <div className="section-b">
                <i className="fas fa-file-upload icon"></i>
              </div>
            </div>
            {selectedClient && (
              <Filter
                client={selectedClient}
                status={fileStatus.STATUS_UPLOADED}
                setSelectedStatus={setSelectedStatus}
              />
            )}
          </div>

          {/* Failed */}
          <div
            className={classnames("box box-danger", {
              selected: selectedStatus === fileStatus.STATUS_UPLOAD_FAILED
            })}
          >
            <div className="content">
              <div className="section-a">
                <div className="number mb-20"> {Math.abs(totalFailed)}</div>
                <div className="description">
                  {Translate({ id: "total_failed" })}{" "}
                </div>
              </div>
              <div className="section-b">
                <i className="fas fa-thumbs-down icon"></i>
              </div>
            </div>
            {selectedClient && (
              <Filter
                client={selectedClient}
                status={fileStatus.STATUS_UPLOAD_FAILED}
                setSelectedStatus={setSelectedStatus}
              />
            )}
          </div>
        </div>
        <div className="action-wrapper1 my-20">
          {hasProperty(() => client.data.total_ready_to_upload_files, false) ? (
            <div className="alert alert-info">
              <i className="fas fa-info-circle mr-10"></i>{" "}
              {Translate({
                id: "files_pending_for_upload",
                values: [
                  isLoadingClient
                    ? "..."
                    : hasProperty(
                        () => client.data.total_ready_to_upload_files,
                        0
                      )
                ]
              })}
            </div>
          ) : null}
          <br />
          <button
            className="btn btn-default fr mb-20"
            onClick={handleRefreshList}
          >
            {Translate({ id: "refresh" })}
          </button>

          <button
            className="btn btn-default"
            onClick={handleRetryFailedUploads}
          >
            {Translate({ id: "retry_failed_uploads" })}
          </button>
        </div>

        {isLoadingFiles && <SpinnerRainbow />}
        {isLoadingFiles === false && (
          <table className="logs">
            <thead>
              <tr>
                <th>{Translate({ id: "file_id" })} </th>
                <th>{Translate({ id: "adapter" })} </th>
                <th>{Translate({ id: "instance_url" })} </th>
                <th>{Translate({ id: "username" })} </th>
                <th>{Translate({ id: "file" })} </th>
                <th>{Translate({ id: "status" })} </th>
                <th>{Translate({ id: "created" })} </th>
                <th>{Translate({ id: "updated" })} </th>
                <th>{Translate({ id: "action" })} </th>
              </tr>
            </thead>
            <tbody>
              {hasData() &&
                files.data.map(file => (
                  <tr className="data" key={file.id}>
                    <td>{file.id}</td>
                    <td>{file.client.adapter.name}</td>
                    <td>{file.client.adapter.url}</td>
                    <td>{file.client.adapter.username}</td>
                    <td>{file.raw_filename}</td>
                    <td>
                      <ReactTooltip />
                      {queueLiveStatus[file.id]
                        ? queueLiveStatus[file.id].toUpperCase()
                        : file.status.toUpperCase()}
                    </td>
                    <td
                      data-tip={moment(
                        file.adapter_props.upload_initiated_at
                      ).calendar()}
                    >
                      {moment(file.adapter_props.upload_initiated_at).fromNow()}
                    </td>
                    <td
                      data-tip={moment(
                        file.adapter_props.upload_modified_at
                      ).calendar()}
                    >
                      {moment(file.adapter_props.upload_modified_at).fromNow()}
                    </td>
                    <td className="center">
                      {has(file, "id") && <UploadLog fileDoc={file} />}
                      {has(file, "id") && <FileDetails fileDoc={file} />}
                      {has(file, "adapter_props.upload_retry_count") &&
                      [
                        fileStatus.STATUS_IN_PROGRESS,
                        fileStatus.STATUS_UPLOAD_FAILED
                      ].includes(file.status) ? (
                        <RetryUpload fileDoc={file} />
                      ) : null}
                    </td>
                  </tr>
                ))}

              {!hasData() && (
                <tr>
                  <td
                    colSpan="9"
                    style={{ textAlign: "center", padding: "20px" }}
                  >
                    <h3>{Translate({ id: "no_logs_to_show" })} </h3>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
        {/* Paginate */}
        {hasData() && has(files, "meta") && (
          <Pagination onPageChange={onPageChange} data={files.meta} />
        )}
      </div>
    </Layout>
  );
}

export default Upload;
